import { CVVC_PROVIDERS } from "@/configuration/groups/stridecare-cvvc/providers";

// Placeholder shared assignee for every CVVC location, pending the real per-location Athena bucket
// names (the index-mapping sheet's Bucket/email column is marked "to be specified by CVVC").
export const CVVC_PLACEHOLDER_ASSIGNEE = "CVVC_PLACEHOLDER_BUCKET";

// Real, provisioned Athena bucket for referral patient cases - all new referrals route to this single
// "needs consult" bucket regardless of location (analogous to StrideCare's hvneedsconsult/
// scfaneedsconsult). Distinct from CVVC_PLACEHOLDER_ASSIGNEE above, which remains an unresolved
// placeholder for other (non-referral-case) assignee needs.
export const CVVC_NEEDS_CONSULT_ASSIGNEE = "cvneedsconsult";

const { ENGLISH, FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR } = CVVC_PROVIDERS;

function providerIds(providers: (typeof CVVC_PROVIDERS)[keyof typeof CVVC_PROVIDERS][]): string[] {
  return providers.map((provider) => provider.athenaProviderId);
}

// assignee is required by the shared AthenaDepartmentConfig shape but unused for CVVC - the real
// "needs consult" case assignee is CVVC_NEEDS_CONSULT_ASSIGNEE, wired directly in athena-config.ts,
// not read from this map (see getDepartmentAssignee's callers, none of which is stridecare-cvvc).
// This object is fed directly into AthenaTenantConfig.core.departments, which is validated against
// the strict athenaDepartmentSchema (configuration/ehr/athena/validation.ts) - it can only carry
// name/assignee/providerGroup, so the provider-eligibility data below lives in a separate map instead
// of extra fields here.
export const departments = {
  "234": { name: "Charleston", assignee: null },
  "235": { name: "Summerville", assignee: null },
  "236": { name: "Mount Pleasant", assignee: null },
  "237": { name: "Walterboro", assignee: null },
  "238": { name: "Murrells Inlet", assignee: null },
  "239": { name: "Orangeburg", assignee: null },
};

export type CvvcDepartmentProviderEligibility = {
  providers: string[];
  defaultProvider: string;
};

// Provider & Location Mapping sheet's eligibility matrix, mirroring StrideCare's
// providerGroups.providers/defaultProvider shape (StridecareDepartmentConfig), kept separate from
// `departments` above since it isn't part of AthenaTenantConfig.core.departments' schema. Every home
// MD except Dr. English is eligible at every location, plus the floating providers Jaecklin,
// Chargualaf, and Herr. Dr. English is eligible only at his own home site (Summerville). Greenwood and
// Peck are FALSE at all 6 locations on the sheet, so they're not listed as eligible anywhere despite
// floating elsewhere in the roster.
export const CVVC_DEPARTMENT_PROVIDER_ELIGIBILITY: Record<string, CvvcDepartmentProviderEligibility> = {
  "234": {
    providers: providerIds([FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: FORTUNA.athenaProviderId,
  },
  "235": {
    providers: providerIds([ENGLISH, FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: ENGLISH.athenaProviderId,
  },
  "236": {
    providers: providerIds([FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: HOCHBERG.athenaProviderId,
  },
  "237": {
    providers: providerIds([FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: FRANZ.athenaProviderId,
  },
  "238": {
    providers: providerIds([FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: HISLOP.athenaProviderId,
  },
  "239": {
    providers: providerIds([FORTUNA, FRANZ, HISLOP, HOCHBERG, KEEFER, JAECKLIN, CHARGUALAF, HERR]),
    defaultProvider: KEEFER.athenaProviderId,
  },
};
